import {useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useTheme, styled} from '@mui/material/styles'
import Popper, {PopperPlacementType} from '@mui/material/Popper'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import CloseIcon from '@mui/icons-material/Close'
import DoneIcon from '@mui/icons-material/Done'
import Autocomplete, {
  AutocompleteCloseReason,
  autocompleteClasses,
} from '@mui/material/Autocomplete'
import InputBase from '@mui/material/InputBase'
import Box from '@mui/material/Box'
import {useQueryClient} from 'react-query'
import {ID, UserAvatar} from '../../../../../../../_metronic/helpers'
import {useAuth} from '../../../../../auth'

export interface IconUserModel {
  name: string
  avatar?: string
  color?: string
  initials?: string
  task_responsible_id?: ID
  user_id: ID
  expected_date?: string | null
}

type Props = {
  users: Array<IconUserModel>
  usersAll: Array<IconUserModel>
  classes?: string
  onChange?: (e: any) => void
  placement?: PopperPlacementType
}

interface PopperComponentProps {
  anchorEl?: any
  disablePortal?: boolean
  open: boolean
}

const StyledAutocompletePopper = styled('div')(({theme}) => ({
  [`& .${autocompleteClasses.paper}`]: {
    boxShadow: 'none',
    margin: 0,
    color: 'inherit',
    fontSize: 13,
  },
  [`& .${autocompleteClasses.listbox}`]: {
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
    padding: 0,
    [`& .${autocompleteClasses.option}`]: {
      minHeight: 'auto',
      alignItems: 'flex-start',
      padding: 8,
      borderBottom: `1px solid  ${theme.palette.mode === 'light' ? ' #eaecef' : '#30363d'}`,
      '&[aria-selected="true"]': {
        backgroundColor: 'transparent',
      },
      [`&.${autocompleteClasses.focused}, &.${autocompleteClasses.focused}[aria-selected="true"]`]:
        {
          backgroundColor: theme.palette.action.hover,
        },
    },
  },
  [`&.${autocompleteClasses.popperDisablePortal}`]: {
    position: 'relative',
  },
}))

function PopperComponent(props: PopperComponentProps) {
  const {disablePortal, anchorEl, open, ...other} = props
  return <StyledAutocompletePopper {...other} />
}
//@ts-ignore
const StyledPopper = styled(Popper)(({theme}) => ({
  border: `1px solid ${theme.palette.mode === 'light' ? '#e1e4e8' : '#30363d'}`,
  boxShadow: `0 8px 24px ${
    theme.palette.mode === 'light' ? 'rgba(149, 157, 165, 0.2)' : 'rgb(1, 4, 9)'
  }`,
  borderRadius: 6,
  width: 300,
  zIndex: theme.zIndex.modal,
  fontSize: 13,
  color: theme.palette.mode === 'light' ? '#24292e' : '#c9d1d9',
  backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#1c2128',
}))

const StyledInput = styled(InputBase)(({theme}) => ({
  padding: 10,
  width: '100%',
  borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
  '& input': {
    borderRadius: 4,
    backgroundColor: theme.palette.mode === 'light' ? '#fff' : '#0d1117',
    padding: 8,
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    border: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
    fontSize: 14,
    '&:focus': {
      boxShadow: '0px 0px 0px 3px rgba(181, 212, 78, 0.35)',
      borderColor: 'var(--tr-green-active)',
    },
  },
}))

export function SequenceResponsiblesSymbolGroup({
  users,
  usersAll,
  classes,
  onChange,
  placement = 'top-end',
}: Props) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [value, setValue] = useState({name: ''} as IconUserModel)
  const [inputValue, setInputValue] = useState('')
  const theme = useTheme()

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    if (anchorEl) anchorEl.focus()

    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const intl = useIntl()

  return (
    <>
      <div className={`symbol-group symbol-hover ${classes}`} onClick={handleClick}>
        {users.length > 0
          ? users.map((user, i) => {
              return i < 3 ? (
                <OverlayTrigger
                  key={`${i}-${user.name}`}
                  placement='top'
                  overlay={<Tooltip id='tooltip-user-name'>{user.name}</Tooltip>}
                >
                  <UserAvatar
                    name={user?.name ?? ''}
                    avatar={user.avatar}
                    sizeClass='symbol-35px'
                    imgClassName='object-fit-cover'
                  />
                </OverlayTrigger>
              ) : null
            })
          : null}

        {users && users.length > 3 ? (
          <div className='symbol symbol-35px symbol-circle'>
            <span className='symbol-label bg-primary text-inverse-primary fw-bolder'>
              +{users.length}
            </span>
          </div>
        ) : null}

        <div className='symbol symbol-35px symbol-circle'>
          <span className='symbol-label bg-primary text-inverse-primary fw-bolder'>+</span>
        </div>
      </div>
      {/* 
        //@ts-ignore */}
      <StyledPopper
        id={value.toString()}
        open={open}
        anchorEl={anchorEl}
        placement={placement}
        disablePortal={true}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <div>
            <Box
              sx={{
                borderBottom: `1px solid ${theme.palette.mode === 'light' ? '#eaecef' : '#30363d'}`,
                padding: '8px 10px',
                fontWeight: 600,
              }}
            >
              {intl.formatMessage({id: 'FORM.INPUT.NAME.ADDUSER_TASK'})}
            </Box>
            <Autocomplete
              value={value}
              inputValue={inputValue}
              onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue)
              }}
              open
              onClose={(event: React.ChangeEvent<{}>, reason: AutocompleteCloseReason) => {
                if (reason === 'escape') {
                  handleClose()
                }
              }}
              onChange={(event, newValue, reason) => {
                if (
                  (event.type === 'keydown' &&
                    (event as React.KeyboardEvent).key === 'Backspace' &&
                    reason === 'removeOption') ||
                  !newValue
                ) {
                  return
                }

                try {
                  onChange && onChange(newValue)
                  setValue({name: ''} as IconUserModel)
                  setInputValue('')
                } catch (ex) {
                  console.error(ex)
                }
              }}
              disableCloseOnSelect
              PopperComponent={PopperComponent}
              renderTags={() => null}
              noOptionsText='No labels'
              renderOption={(props, option, {selected}) => (
                <li {...props}>
                  <Box sx={{mr: 1, mt: '2px', flexShrink: 0}}>
                    <UserAvatar
                      name={option?.name ?? ''}
                      avatar={option.avatar}
                      sizeClass='symbol-20px'
                      symbolLabelClass='me-2'
                      imgClassName='object-fit-cover'
                    />
                  </Box>
                  <Box
                    sx={{
                      flexGrow: 1,
                      '& span': {
                        color: theme.palette.mode === 'light' ? '#586069' : '#8b949e',
                      },
                    }}
                  >
                    {option.name}
                    <br />
                  </Box>
                </li>
              )}
              options={usersAll?.sort((a, b) => {
                // Display the selected labels first.
                let ai = usersAll.indexOf(a)
                ai = ai === -1 ? usersAll.length + usersAll.indexOf(a) : ai
                let bi = usersAll.indexOf(b)
                bi = bi === -1 ? usersAll.length + usersAll.indexOf(b) : bi
                return ai - bi
              })}
              isOptionEqualToValue={(option, value) =>
                //@ts-ignore
                option.user_id == (value.user_id ?? value.id)
              }
              getOptionLabel={(option) => option.name}
              renderInput={(params) => (
                <StyledInput
                  ref={params.InputProps.ref}
                  inputProps={params.inputProps}
                  autoFocus
                  placeholder={intl.formatMessage({id: 'FORM.INPUT.NAME.SELECTUSER_TEAMS'})}
                />
              )}
            />
          </div>
        </ClickAwayListener>
      </StyledPopper>
    </>
  )
}
